import type { FileRoutesByTo } from '../routeTree.gen'

/**
 * Erased at build time, so this module stays importable from a node-only vitest run — but it makes
 * a renamed or deleted route a compile error here rather than a 404 a visitor finds first.
 */
type AppPath = keyof FileRoutesByTo

export type HeaderNavLinkInput = { href: string; label: string }

export type HeaderNavLink = { kind: 'link'; href: string; label: string }

export type HeaderNavGroup = {
    kind: 'group'
    id: string
    label: string
    items: HeaderNavLink[]
}

export type HeaderNavEntry = HeaderNavLink | HeaderNavGroup

export type HeaderNavLabels = {
    services: string
    camps: string
    programs: string
    packages: string
    videoConsultations: string
    careExaminations: string
    webinars: string
    knowledgeBase: string
    about: string
    forExperts: string
    contact: string
    pages: string
}

/**
 * Videókonzultáció and Vizsgálat foglalás go straight into the booking flows rather than to the
 * `/video-consultations` and `/care-examinations` catalogues, so the menu and the Időpontfoglalás
 * modal lead to the same place. Both sit behind `/booking`'s auth guard, which bounces a signed-out
 * visitor to the login form and returns them to the flow afterwards.
 */
const SERVICE_PATHS = [
    '/events',
    '/lifestyle-programs',
    '/packages',
    '/booking/video-consultation',
    '/booking/examination',
    '/webinars',
] as const satisfies readonly AppPath[]

const KNOWLEDGE_BASE_PATH = '/knowledge-base' satisfies AppPath
const ABOUT_PATH = '/about' satisfies AppPath
const FOR_EXPERTS_PATH = '/for-experts' satisfies AppPath
const CONTACT_PATH = '/contact' satisfies AppPath

const link = (href: string, label: string): HeaderNavLink => ({ kind: 'link', href, label })

/** Every href the navigation reaches, groups flattened. */
export const flattenHeaderNavPaths = (entries: readonly HeaderNavEntry[]): string[] =>
    entries.flatMap((entry) =>
        entry.kind === 'group' ? entry.items.map((item) => item.href) : [entry.href],
    )

/**
 * The editor-authored label, or the product's own when the editor never supplied one.
 *
 * `useWebContent.getText` answers `''` for a key the cms row does not carry, which is how blanking a
 * field in admin used to leave a nameless gap in the header.
 */
export const preferCmsLabel = (cmsValue: string, fallback: string): string =>
    cmsValue.trim() === '' ? fallback : cmsValue

/** A group counts as current when the page being viewed is one of its members. */
export const isHeaderNavEntryActive = (entry: HeaderNavEntry, pathname: string): boolean =>
    entry.kind === 'group'
        ? entry.items.some((item) => item.href === pathname)
        : entry.href === pathname

/**
 * The header's navigation, as data.
 *
 * Desktop renders a group as a dropdown and mobile as a collapsible, so the structure has to live
 * somewhere neither of them owns. Keeping it here also keeps it testable: `apps/web`'s vitest config
 * is node-only and collects `*.test.ts`, so a component is the one shape that cannot be asserted.
 *
 * Editor-authored entries are appended to the product's own navigation, never substituted for it: a
 * cms menu is how a cms page gets reachable, not a way to take the rest of the site out of the
 * header by accident. They are deduplicated against the *flattened* product paths — most of those
 * now sit inside a group, and a hand-kept list of top-level hrefs would no longer see them.
 *
 * `pageLinks` is the complete index of published cms pages and is deliberately *not* deduplicated
 * against the curated entries above: the curated entry is emphasis, and this menu is the whole list
 * — an editor who highlights one page must not thereby remove it from the list of all of them.
 */
export const buildHeaderNav = ({
    labels,
    cmsLinks,
    pageLinks,
}: {
    labels: HeaderNavLabels
    cmsLinks: readonly HeaderNavLinkInput[] | null
    pageLinks: readonly HeaderNavLinkInput[]
}): HeaderNavEntry[] => {
    const [camps, programs, packages, videoConsultations, careExaminations, webinars] =
        SERVICE_PATHS

    const productNav: HeaderNavEntry[] = [
        {
            kind: 'group',
            id: 'services',
            label: labels.services,
            items: [
                link(camps, labels.camps),
                link(programs, labels.programs),
                link(packages, labels.packages),
                link(videoConsultations, labels.videoConsultations),
                link(careExaminations, labels.careExaminations),
                link(webinars, labels.webinars),
            ],
        },
        link(KNOWLEDGE_BASE_PATH, labels.knowledgeBase),
        {
            kind: 'group',
            id: 'about',
            label: labels.about,
            items: [link(ABOUT_PATH, labels.about), link(FOR_EXPERTS_PATH, labels.forExperts)],
        },
        link(CONTACT_PATH, labels.contact),
    ]

    const productPaths = new Set(flattenHeaderNavPaths(productNav))

    return [
        ...productNav,
        ...(cmsLinks ?? [])
            .filter((cmsLink) => !productPaths.has(cmsLink.href))
            .map((cmsLink) => link(cmsLink.href, cmsLink.label)),
        // A menu with no entries is a dead control, and a site with no cms pages should look
        // untouched — so the group simply does not exist rather than opening onto nothing.
        ...(pageLinks.length > 0
            ? [
                  {
                      kind: 'group' as const,
                      id: 'pages',
                      label: labels.pages,
                      items: pageLinks.map((page) => link(page.href, page.label)),
                  },
              ]
            : []),
    ]
}
